import LoadingScreen from '@/components/LoadingScreen'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '@/hooks/useRedux'
import { userService } from '@/services/api'
import type { User } from '@/types'

export default function ProfilePage() {
  const { userId } = useParams<{ userId: string }>()
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [profile, setProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    display_name: '',
    custom_status: '',
    avatar_url: '',
  })

  useEffect(() => {
    loadProfile()
  }, [userId])

  const loadProfile = async () => {
    setLoading(true)
    try {
      const targetId = userId || currentUser?.id
      if (!targetId) return
      
      const response = await userService.get(targetId)
      setProfile(response.data)
      setEditForm({
        display_name: response.data.display_name || '',
        custom_status: response.data.custom_status || '',
        avatar_url: response.data.avatar_url || '',
      })
    } catch (error) {
      console.error('Failed to load profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!profile) return
    try {
      const response = await userService.updateProfile(profile.id, editForm)
      setProfile(response.data)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update profile:', error)
      alert('Failed to update profile')
    }
  }

  const isOwnProfile = !userId || userId === currentUser?.id

  if (loading) {
    return <LoadingScreen label="Loading profile…" />
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <p className="text-white">User not found</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-purple-900 to-blue-900 rounded-lg p-8 mb-8">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <img
                src={profile.avatar_url || '/default-avatar.png'}
                alt={profile.username || 'User'}
                className="w-32 h-32 rounded-full border-4 border-white"
              />
              <div
                className={`absolute bottom-2 right-2 w-6 h-6 rounded-full border-4 border-white ${
                  profile.status === 'online'
                    ? 'bg-green-500'
                    : profile.status === 'away'
                    ? 'bg-yellow-500'
                    : profile.status === 'dnd'
                    ? 'bg-red-500'
                    : 'bg-gray-500'
                }`}
              />
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-white mb-2">
                {profile.display_name || profile.username || 'Unknown User'}
              </h1>
              <p className="text-gray-300">@{profile.username}</p>
              {profile.custom_status && (
                <p className="text-gray-400 mt-2">{profile.custom_status}</p>
              )}
              {profile.verified_domain && (
                <p className="text-purple-400 mt-2">✓ {profile.verified_domain}</p>
              )}
            </div>

            {/* Edit Button */}
            {isOwnProfile && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-6 rounded"
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            )}
          </div>
        </div>

        {/* Edit Form */}
        {isEditing && (
          <div className="bg-gray-800 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Edit Profile</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, display_name: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Custom Status
                </label>
                <input
                  type="text"
                  value={editForm.custom_status}
                  onChange={(e) =>
                    setEditForm({ ...editForm, custom_status: e.target.value })
                  }
                  placeholder="What's on your mind?"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Avatar URL
                </label>
                <input
                  type="text"
                  value={editForm.avatar_url}
                  onChange={(e) =>
                    setEditForm({ ...editForm, avatar_url: e.target.value })
                  }
                  placeholder="https://example.com/avatar.png"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>

              <button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* Profile Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-gray-400">Friends</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <p className="text-3xl font-bold text-white">0</p>
            <p className="text-gray-400">Servers</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 text-center">
            <p className="text-3xl font-bold text-white">
              {profile.premium_tier || 'free'}
            </p>
            <p className="text-gray-400">Tier</p>
          </div>
        </div>

        {/* Activity Section */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Recent Activity</h2>
          <p className="text-gray-400">No recent activity</p>
        </div>
      </div>
    </div>
  )
}
